// React & Nextjs imports
import React, { memo, useMemo } from "react";

// Models & Constants
import { UsStock } from "@/app/interface/global";

// Hooks & Utils & Contexts
import { cn } from "@/lib/utils";
import { useCulture } from "@/context/cultureProvider";
import { formatCurrency } from "@/app/utils/currency-formatter";
import useMobile from "@/app/hooks/use-mobile";

interface Props {
    stockDetails: UsStock;
}

interface PerformancePositionSliderProps {
    label_low: string;
    label_high: string;
    value_low: number;
    value_high: number;
    percentage_position?: number;
}

const PerformancePositionSlider = ({
    label_low,
    label_high,
    value_low,
    value_high,
    percentage_position,
}: PerformancePositionSliderProps) => {
    const { cultureContext } = useCulture();
    const { isMobile, hasLoaded } = useMobile();

    return (
        <div className="mt-3">
            <div className="flex flex-col gap-3 pb-6">
                <div className="w-full">
                    <div className="mb-3 flex justify-between">
                        <div className="text-left leading-[140%] tracking-[0%]">
                            <span className="-mb-1 block text-2xs font-semibold uppercase text-[#494F6E] md:mb-1.5 md:text-sm md:font-normal md:normal-case">
                                {label_low}
                            </span>
                            <span className="] block text-base font-medium text-[#0F1016]">
                                {formatCurrency(
                                    value_low,
                                    cultureContext.currencyCode,
                                    cultureContext.locale,
                                ) || "--"}
                            </span>
                        </div>
                        <div className="text-right leading-[140%] tracking-[0%]">
                            <span className="-mb-1 block text-2xs font-semibold uppercase text-[#494F6E] md:mb-1.5 md:text-sm md:font-normal md:normal-case">
                                {label_high}
                            </span>
                            <span className="] block text-base font-medium text-[#0F1016]">
                                {formatCurrency(
                                    value_high,
                                    cultureContext.currencyCode,
                                    cultureContext.locale,
                                ) || "--"}
                            </span>
                        </div>
                    </div>
                    {/* {percentage_position} */}
                    {percentage_position !== undefined ? (
                        <div className="relative">
                            <div className="h-[5px] rounded bg-[#0E0F171A]"></div>
                            <div
                                className={cn(
                                    "absolute -top-[3.5px] h-[12px] w-1 rounded-[9px] bg-[#0F1016] transition-all duration-300 ease-in md:w-[30px]",
                                )}
                                style={{
                                    left:
                                        Number(percentage_position) === 0
                                            ? "0%"
                                            : Math.abs(Number(percentage_position)) < 3
                                              ? `${Math.abs(Number(percentage_position)) + 3}%`
                                              : `${Math.abs(Number(percentage_position) + (hasLoaded && isMobile ? 0 : -3))}%`,
                                    opacity: Number(percentage_position) === 0 ? 1 : 1,
                                }}
                            ></div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const PerformancePosition = ({ stockDetails }: Props) => {
    const percentagePositionDayWise = useMemo(() => {
        const percentage = Math.min(
            Math.max(
                ((stockDetails?.live_price - stockDetails?.price_movement?.days_low) /
                    (stockDetails?.price_movement?.days_high -
                        stockDetails?.price_movement?.days_low)) *
                    100,
                0,
            ),
            100,
        );

        return percentage || 0;
    }, [
        stockDetails?.price_movement?.days_low,
        stockDetails?.price_movement?.days_high,
        stockDetails?.live_price,
    ]);

    const percentagePosition52WeekWise = useMemo(() => {
        const percentage = Math.min(
            Math.max(
                ((stockDetails?.live_price - stockDetails?.company_fundamentals?.low_52_week) /
                    (stockDetails?.company_fundamentals?.high_52_week -
                        stockDetails?.company_fundamentals?.low_52_week)) *
                    100,
                0,
            ),
            100,
        );

        return percentage || 0;
    }, [
        stockDetails?.company_fundamentals?.low_52_week,
        stockDetails?.company_fundamentals?.high_52_week,
        stockDetails?.live_price,
    ]);

    return (
        <div className="mt-10 md:mt-20">
            <div className="flex flex-col gap-3 pb-6">
                <h3 className="hidden text-xl font-semibold sm:text-3xl md:block">Performance</h3>

                {(stockDetails?.price_movement?.days_low ||
                    stockDetails?.price_movement?.days_high) && (
                    <PerformancePositionSlider
                        label_low="Day's Low"
                        label_high="Day's High"
                        value_low={stockDetails?.price_movement?.days_low}
                        value_high={stockDetails?.price_movement?.days_high}
                        percentage_position={
                            percentagePositionDayWise /* stockDetails?.price_movement?.one_day_percentage */
                        }
                    />
                )}

                {(stockDetails?.company_fundamentals?.low_52_week && 
                    stockDetails?.company_fundamentals?.high_52_week) && (
                    <PerformancePositionSlider
                        label_low="52 Week's Low"
                        label_high="52 Week's High"
                        value_low={stockDetails?.company_fundamentals?.low_52_week}
                        value_high={stockDetails?.company_fundamentals?.high_52_week}
                        percentage_position={
                            percentagePosition52WeekWise /* stockDetails?.company_fundamentals?.eps */
                        }
                    />
                )}

                {(stockDetails?.price_movement?.open ||
                    stockDetails?.price_movement?.previous_close) && (
                    <PerformancePositionSlider
                        label_low="Open"
                        label_high="Prev. Close"
                        value_low={stockDetails?.price_movement?.open || 0}
                        value_high={stockDetails?.price_movement?.previous_close || 0}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(PerformancePosition);
